import React, {useContext} from 'react';
import classnames from 'classnames';
import {List} from 'immutable';

import QuestionsList from 'components/PracticeView/QuestionsList/QuestionsList.react';
import QuestionsListFooter from 'components/PracticeView/QuestionsList/QuestionsListFooter.react';
import {MobilePracticeViewContext} from 'components/PracticeView/MobilePracticeViewProvider.context';
import {canSaveQuestionSetToFolder} from 'components/PracticeView/practiceView.utils';

import FolderPracticeViewQuestionListHeader from './FolderPracticeViewQuestionListHeader.react';

interface Props {
  activeQuestion: any;
  selectedQuestionCount: number;
  makeQuestionSetOnChangeHandler: (questionSet: any) => void;
  selectedQuestionSets: List<any>;
  isSelectAllQuestionSetsMode: boolean;
  isRearranging: boolean;
  toggleSelectAllQuestionSets: (e: any) => void;
  onQuestionClick: ({question, questionSet}: {question: any; questionSet: any}) => () => void;
  isLoading: boolean;
  scrollToQuestionById: React.RefObject<any>;
  paginatedQuestionsListManager: any;
  questionSets: any;
}

export const FolderPracticeViewQuestionList = ({
  activeQuestion,
  selectedQuestionCount,
  makeQuestionSetOnChangeHandler,
  selectedQuestionSets,
  isSelectAllQuestionSetsMode,
  isRearranging,
  toggleSelectAllQuestionSets,
  onQuestionClick,
  isLoading,
  scrollToQuestionById,
  paginatedQuestionsListManager,
  questionSets
}: Props) => {
  const {isQuestionsListInView, setIsQuestionsListInView, isMobile} =
    useContext(MobilePracticeViewContext);

  const activeSubject = activeQuestion.getQuestionSet().getSubject();

  // taken directly from SPV
  const isQuestionSetSelected = (questionSet) => {
    const isIncluded = selectedQuestionSets.includes(questionSet.getId());
    return isSelectAllQuestionSetsMode ? !isIncluded : isIncluded;
  };

  const makeMobileOnQuestionClick = ({question, questionSet}) => {
    return () => {
      if (isMobile) {
        // Only checking if its mobile because component would need to be rerendered to detect changes
        // it's less resource-intensive to assume that if this is being fired, the questions list is in view
        // and always dismiss it as false.
        setIsQuestionsListInView(false);
      }
      onQuestionClick({question, questionSet})();
    };
  };

  // ### START PROPS ###
  const questionsListProps = {
    scrollToQuestionById,
    onScrollLastQuestionIntoView: async () => {
      if (!paginatedQuestionsListManager.isRequestInFlight) {
        await paginatedQuestionsListManager.fetchNextPage();
      }
    },
    scrollToTop: false,
    header: (
      <FolderPracticeViewQuestionListHeader
        selectedQuestionCount={selectedQuestionCount}
        isSelectAllQuestionSetsMode={isSelectAllQuestionSetsMode}
        isRearranging={isRearranging}
        toggleSelectAllQuestionSets={toggleSelectAllQuestionSets}
      />
    ),
    footer: (
      <QuestionsListFooter
        activeQuestion={activeQuestion}
        setActiveQuestionState={(question, questionSet) => {
          const onQuestionClickFunction = onQuestionClick({question, questionSet});
          onQuestionClickFunction();
        }}
        scrollToQuestionById={scrollToQuestionById}
        paginatedQuestionsListManager={paginatedQuestionsListManager}
      />
    )
  };

  const questionSetProps = {
    selectable: (questionSet) => canSaveQuestionSetToFolder(activeSubject, questionSet),
    isSelected: isQuestionSetSelected,
    questionSetOnChangeHandler: makeQuestionSetOnChangeHandler,
    rearrangeable: isRearranging,
    viewingFromFolder: true,
    selectedQuestionSets,
    isSelectAllQuestionSetsMode
  };
  // ### END PROPS ###

  if (!paginatedQuestionsListManager) {
    return null;
  }

  return (
    <QuestionsList
      {...questionsListProps}
      className={classnames(
        'practice-view__questions-list u-display_flex u-flex-direction_column',
        {
          'practice-view__questions-list--in-view': isQuestionsListInView
        }
      )}
      activeQuestion={activeQuestion}
      questionSetProps={{
        ...questionSetProps,
        hasQuestionSetAccessPredicate: () => true
      }}
      questionProps={{
        showDifficulty: true,
        onQuestionClick: (args) => makeMobileOnQuestionClick(args)
      }}
      questionSets={questionSets}
      isLoading={isLoading}
    />
  );
};

export default FolderPracticeViewQuestionList;
